<template>
  <div class="card">
    <div class="card-body">
      <form @submit.prevent="save">
        <!-- Order Information -->
        <h5 class="mb-3">Order Information</h5>
        <div class="row">
          <div class="col-md-4 mb-3">
            <label class="form-label">Customer</label>
            <div class="row">
              <div class="col-lg-10 col-sm-10 col-10">
                <select
                  v-model="form.customer_id"
                  class="form-select"
                  :class="{ 'is-invalid': errors.customer_id }"
                >
                  <option value="">Select Customer</option>
                  <option
                    v-for="customer in customers"
                    :key="customer.id"
                    :value="customer.id"
                  >
                    {{ customer.name }}
                  </option>
                </select>
                <div v-if="errors.customer_id" class="invalid-feedback">
                  {{ errors.customer_id[0] }}
                </div>
              </div>
              <div class="col-lg-2 col-sm-2 col-2 ps-0">
                <div class="add-icon">
                  <a href="#" @click.prevent="openCustomerModal" class="choose-add">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                         viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                         stroke-linecap="round" stroke-linejoin="round"
                         class="feather feather-plus-circle plus">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="16"></line>
                      <line x1="8" y1="12" x2="16" y2="12"></line>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-4 mb-3">
            <label class="form-label">Order Date</label>
            <input
              v-model="form.order_date"
              type="date"
              class="form-control"
              :class="{ 'is-invalid': errors.order_date }"
            >
            <div v-if="errors.order_date" class="invalid-feedback">
              {{ errors.order_date[0] }}
            </div>
          </div>

          <div class="col-md-4 mb-3">
            <label class="form-label">Order Delivery Date</label>
            <input
              v-model="form.delivery_date"
              type="date"
              class="form-control"
              :class="{ 'is-invalid': errors.delivery_date }"
            >
            <div v-if="errors.delivery_date" class="invalid-feedback">
              {{ errors.delivery_date[0] }}
            </div>
          </div>
        </div>

        <!-- Product Items -->
        <div class="row mt-4">
          <div class="col-12">
            <h5 class="mb-3">Product Items</h5>
            <div class="table-responsive">
              <table class="table">
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>Dimensions (L×W×H)</th>
                    <th>Color</th>
                    <th>Image</th>
                    <th>Selling Price</th>
                    <th>Quantity</th>
                    <th>Total</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(item, index) in form.items" :key="index">
                    <tr>
                      <td>
                        <input
                          v-model="item.product_name"
                          type="text"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`items.${index}.product_name`] }"
                          placeholder="Product Name"
                        >
                        <div v-if="errors[`items.${index}.product_name`]" class="invalid-feedback">
                          {{ errors[`items.${index}.product_name`][0] }}
                        </div>
                      </td>
                      <td>
                        <div class="input-group">
                          <input
                            v-model.number="item.length"
                            type="number"
                            class="form-control"
                            placeholder="L"
                            step="0.01"
                          >
                          <input
                            v-model.number="item.width"
                            type="number"
                            class="form-control"
                            placeholder="W"
                            step="0.01"
                          >
                          <input
                            v-model.number="item.height"
                            type="number"
                            class="form-control"
                            placeholder="H"
                            step="0.01"
                          >
                        </div>
                      </td>
                      <td>
                        <input
                          v-model="item.color"
                          type="text"
                          class="form-control"
                          placeholder="Color"
                        >
                      </td>
                      <td>
                        <input
                          @change="handleImageUpload(index, $event)"
                          type="file"
                          class="form-control"
                          accept="image/*"
                        >
                      </td>
                      <td>
                        <input
                          v-model.number="item.selling_price"
                          @input="updatePrice(index)"
                          type="number"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`items.${index}.selling_price`] }"
                          step="0.01"
                          min="0"
                        >
                      </td>
                      <td>
                        <input
                          v-model.number="item.quantity"
                          @input="updateQuantity(index)"
                          type="number"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`items.${index}.quantity`] }"
                          min="1"
                        >
                      </td>
                      <td>{{ formatCurrency(item.total) }}</td>
                      <td>
                        <button
                          type="button"
                          @click="removeItem(index)"
                          class="btn btn-sm btn-danger"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      </td>
                    </tr>
                    <tr>
                      <td colspan="8">
                        <textarea
                          v-model="item.notes"
                          class="form-control"
                          placeholder="Item Notes"
                          rows="1"
                        ></textarea>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
            <div class="text-center mt-3">
              <button type="button" @click="addItem" class="btn btn-primary">
                <i class="feather-plus"></i> Add Product
              </button>
            </div>
          </div>
        </div>

        <!-- Payment Information -->
        <div class="row mt-4">
          <div class="col-12">
            <h5 class="mb-3">Payment Information</h5>
          </div>
          <div class="col-md-4 mb-3">
            <label class="form-label">Total Amount</label>
            <input type="text" class="form-control" :value="formatCurrency(totals.total_amount)" readonly>
          </div>

          <div class="col-md-4 mb-3">
            <label class="form-label">Discount %</label>
            <input
              v-model.number="form.discount"
              @input="calculateTotalAmount"
              type="number"
              class="form-control"
              :class="{ 'is-invalid': errors.discount }"
              step="0.01"
              min="0"
            >
            <div v-if="errors.discount" class="invalid-feedback">
              {{ errors.discount[0] }}
            </div>
          </div>

          <div class="col-md-4 mb-3">
            <label class="form-label">Paid Amount</label>
            <input
              v-model.number="form.paid_amount"
              @input="calculateDueAmount"
              type="number"
              class="form-control"
              :class="{ 'is-invalid': errors.paid_amount }"
              step="0.01"
              min="0"
            >
            <div v-if="errors.paid_amount" class="invalid-feedback">
              {{ errors.paid_amount[0] }}
            </div>
          </div>

          <div class="col-md-4 mb-3">
            <label class="form-label">Due Amount</label>
            <input type="text" class="form-control" :value="formatCurrency(totals.due_amount)" readonly>
          </div>

          <div class="col-md-12 mb-3">
            <label class="form-label">Order Notes</label>
            <textarea
              v-model="form.notes"
              class="form-control"
              rows="3"
              placeholder="Order Notes"
            ></textarea>
          </div>
        </div>

        <div class="text-end mt-4">
          <button type="submit" class="btn btn-primary" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            Create Order
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { showSuccessMessage, showErrorMessage } from '../utils/notifications.js';

export default {
  name: 'OrderCreate',

  data() {
    return {
      form: {
        customer_id: '',
        order_date: new Date().toISOString().split('T')[0],
        delivery_date: new Date().toISOString().split('T')[0],
        notes: '',
        paid_amount: 0,
        discount: 0,
        items: []
      },
      customers: [],
      errors: {},
      loading: false
    };
  },

  computed: {
    totals() {
      const subtotal = this.form.items.reduce((sum, item) => sum + (item.total || 0), 0);
      const discount_amount = subtotal * (this.form.discount / 100);
      const total_amount = subtotal - discount_amount;
      const due_amount = total_amount - (this.form.paid_amount || 0);

      return {
        subtotal,
        discount_amount,
        total_amount,
        due_amount
      };
    }
  },

  async mounted() {
    console.log('OrderCreate component mounted');

    await this.loadCustomers();
    this.addItem(); // Add initial item

    // Mark element as mounted
    this.$el.closest('[data-vue-component]')?.classList.add('vue-mounted');
  },

  methods: {
    async loadCustomers() {
      try {
        const response = await this.$http.get('/api/customers');
        if (response.data.success) {
          this.customers = response.data.customers.data || response.data.customers;
        }
      } catch (error) {
        console.error('Failed to load customers:', error);
        this.showErrorMessage('Failed to load customers');
      }
    },

    addItem() {
      this.form.items.push({
        product_name: '',
        length: null,
        width: null,
        height: null,
        color: '',
        product_image: null,
        cost_price: 0,
        selling_price: 0,
        quantity: 1,
        total: 0,
        notes: ''
      });
    },

    removeItem(index) {
      this.form.items.splice(index, 1);
      this.calculateTotalAmount();
    },

    updatePrice(index) {
      const item = this.form.items[index];
      item.total = (item.selling_price || 0) * (item.quantity || 0);
      this.calculateTotalAmount();
    },

    updateQuantity(index) {
      const item = this.form.items[index];
      item.total = (item.selling_price || 0) * (item.quantity || 0);
      this.calculateTotalAmount();
    },

    calculateTotalAmount() {
      // Totals are computed automatically
    },

    calculateDueAmount() {
      // Due amount is computed automatically
    },

    handleImageUpload(index, event) {
      const file = event.target.files[0];
      if (file) {
        this.form.items[index].product_image = file;
      }
    },

    openCustomerModal() {
      // Emit event to open customer modal
      this.$emit('open-customer-modal');

      // Or trigger modal directly if available
      const modal = document.getElementById('add-customer-vue');
      if (modal) {
        const modalInstance = new bootstrap.Modal(modal);
        modalInstance.show();
      }
    },

    async save() {
      this.loading = true;
      this.errors = {};

      try {
        // Prepare form data for file uploads
        const formData = new FormData();

        // Add basic form fields
        formData.append('customer_id', this.form.customer_id);
        formData.append('order_date', this.form.order_date);
        formData.append('delivery_date', this.form.delivery_date);
        formData.append('notes', this.form.notes || '');
        formData.append('paid_amount', this.form.paid_amount || 0);
        formData.append('discount', this.form.discount || 0);
        formData.append('total_amount', this.totals.total_amount);

        // Add items
        this.form.items.forEach((item, index) => {
          formData.append(`items[${index}][product_name]`, item.product_name);
          formData.append(`items[${index}][length]`, item.length || '');
          formData.append(`items[${index}][width]`, item.width || '');
          formData.append(`items[${index}][height]`, item.height || '');
          formData.append(`items[${index}][color]`, item.color || '');
          formData.append(`items[${index}][selling_price]`, item.selling_price || 0);
          formData.append(`items[${index}][quantity]`, item.quantity || 1);
          formData.append(`items[${index}][total]`, item.total || 0);
          formData.append(`items[${index}][notes]`, item.notes || '');

          if (item.product_image) {
            formData.append(`items[${index}][product_image]`, item.product_image);
          }
        });

        const response = await this.$http.post('/api/orders', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          this.showSuccessMessage(response.data.message);

          // Emit event for parent components
          this.$emit('order-created', response.data.order);

          // Redirect if specified
          if (response.data.redirect_url) {
            window.location.href = response.data.redirect_url;
          } else {
            // Refresh page to show new order
            window.location.reload();
          }
        }
      } catch (error) {
        console.error('Order creation failed:', error);

        if (error.response?.status === 422) {
          this.errors = error.response.data.errors || {};
          this.showErrorMessage('Please check the form for errors');
        } else {
          this.showErrorMessage(
            error.response?.data?.message || 'An error occurred while creating the order'
          );
        }
      } finally {
        this.loading = false;
      }
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    },

    showSuccessMessage,
    showErrorMessage
  }
}
</script>

<style scoped>
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #dc3545;
}

.add-icon .choose-add {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid #ddd;
  border-radius: 4px;
  color: #666;
  text-decoration: none;
}

.add-icon .choose-add:hover {
  background-color: #f8f9fa;
  color: #007bff;
}
</style>
